import type Transaction from "@/classes/Transaction.ts";
import type { ByPeriod, Currency, Totals } from "@/types/transaction";

const format = (amount: number | null | undefined, currency: Currency = "EUR") => {
  if (!amount || isNaN(amount)) {
    amount = 0;
  }

  return Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency,
  }).format(amount / 100);
};

// Disabled transactions don't count towards any displayed amount. This is the single place
// that rule is defined: the store's totals and both views' per-name cells go through it.
const sum = (transactions: Transaction[] | undefined) =>
  (transactions ?? [])
    .filter((tx) => !tx.isDisabled)
    .reduce((acc: number, tx) => acc + tx.amount, 0);

// `byDay`/`byMonth` only hold an entry for periods that had transactions, but the cumulative
// rows (Saldo) have to carry over from the last period before `key` — a day
// without income or expense still has yesterday's balance. Keys are zero-padded ISO dates, so
// a string comparison is chronological. Periods before the first entry carry initialBalance.
const totalsOn = (periods: ByPeriod, key: string, initialBalance: number): Totals => {
  const entry = periods[key];

  const nearest = Object.keys(periods)
    .filter((period) => period < key)
    .sort()
    .pop();

  const carried = nearest ? periods[nearest].totals : undefined;

  return {
    in: entry?.totals.in ?? 0,
    out: entry?.totals.out ?? 0,
    balance: entry?.totals.balance ?? carried?.balance ?? initialBalance,
    reservations: entry?.totals.reservations ?? carried?.reservations ?? 0,
  };
};

// Cumulative reserved fund at `key` ("YYYY-MM" or "YYYY-MM-DD"): the opening balance from
// before the fetched window plus every reservation delta on or before that key.
const reservedThrough = (
  opening: number,
  byDay: Record<string, number> | undefined,
  key: string,
): number => {
  let total = opening;

  for (const [day, delta] of Object.entries(byDay ?? {})) {
    if (key.length === 7 ? day.slice(0, 7) <= key : day <= key) {
      total += delta;
    }
  }

  return total;
};

export { format, reservedThrough, sum, totalsOn };
